import { z } from 'zod'

export const CRM_LEAD_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM lead read alias. The route keeps the
 * product's existing validation/precedence rules; this schema describes the
 * wire shape without replacing those domain validators. */
export const crmLeadReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  ownerUserId: optionalQueryString,
  owner_user_id: optionalQueryString,
  customerId: optionalQueryString,
  source: optionalQueryString,
  stage: optionalQueryString,
  program: optionalQueryString,
  program_interest: optionalQueryString,
  unassigned: optionalQueryString,
  page: optionalQueryString,
  offset: optionalQueryString,
  limit: optionalQueryString,
})

export const crmLeadFrontendFieldsSchema = z.object({
  id: z.number().finite(),
  first_name: nullableString,
  last_name: nullableString,
  email: nullableString,
  phone: nullableString,
  phone_raw: nullableString,
  phone_e164: nullableString,
  company: nullableString,
  source: nullableString,
  program_interest: nullableString,
  title: nullableString,
  stage: z.string(),
  status: z.string(),
  owner_user_id: z.number().finite().nullable(),
  owner_name: nullableString,
  notes: nullableString,
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough()

export const crmLeadSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  createdByUserId: z.string().min(1),
  ownerUserId: z.string().nullable(),
  companyName: z.string(),
  contactName: nullableString,
  firstName: nullableString,
  lastName: nullableString,
  contactEmail: nullableString,
  phone: nullableString,
  source: nullableString,
  programInterest: nullableString,
  title: nullableString,
  notes: nullableString,
  stage: z.string(),
  status: z.string(),
  stageReason: nullableString,
  tags: z.array(z.string()),
  active: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

export const crmLeadReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmLeadReadEvidenceSchema = z.object({
  sourceOfTruth: z.literal('DB1'),
  tablesRead: z.tuple([z.literal('crm_leads')]),
  projection: z.enum(['crm-lead-list', 'crm-lead-detail']),
  rawPayloadReturned: z.literal(false),
  rawLeadNotesReturned: z.literal(false),
}).passthrough()

export const crmLeadListItemSchema = crmLeadFrontendFieldsSchema
  .omit({ id: true })
  .merge(crmLeadSummarySchema)
  .extend({ _degraded: z.literal(true).optional() })

export const crmLeadListResponseSchema = z.object({
  contract_version: z.literal(CRM_LEAD_CONTRACT_VERSION),
  leads: z.array(crmLeadListItemSchema),
  leadEvidence: crmLeadReadEvidenceSchema.extend({ projection: z.literal('crm-lead-list') }),
  sideEffects: crmLeadReadSideEffectsSchema,
}).passthrough()

export const crmLeadDetailResponseSchema = crmLeadFrontendFieldsSchema.extend({
  contract_version: z.literal(CRM_LEAD_CONTRACT_VERSION),
  lead: crmLeadSummarySchema,
  leadEvidence: crmLeadReadEvidenceSchema.extend({ projection: z.literal('crm-lead-detail') }),
  sideEffects: crmLeadReadSideEffectsSchema,
}).passthrough()

export const CRM_LEAD_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
  'CR_IDEMPOTENCY_KEY_REUSED',
] as const

export const crmLeadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_LEAD_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }),
})

export type CrmLeadReadQuery = z.infer<typeof crmLeadReadQuerySchema>
export type CrmLeadFrontendFields = z.infer<typeof crmLeadFrontendFieldsSchema>
export type CrmLeadSummary = z.infer<typeof crmLeadSummarySchema>
export type CrmLeadReadSideEffects = z.infer<typeof crmLeadReadSideEffectsSchema>
export type CrmLeadReadEvidence = z.infer<typeof crmLeadReadEvidenceSchema>
export type CrmLeadListItem = z.infer<typeof crmLeadListItemSchema>
export type CrmLeadListResponse = z.infer<typeof crmLeadListResponseSchema>
export type CrmLeadDetailResponse = z.infer<typeof crmLeadDetailResponseSchema>
export type CrmLeadErrorCode = (typeof CRM_LEAD_ERROR_CODES)[number]
export type CrmLeadErrorEnvelope = z.infer<typeof crmLeadErrorEnvelopeSchema>
